import { useState } from "react";
import { toast } from "sonner";
import { Check, Copy, Eye, EyeOff } from "lucide-react";

const backend = process.env.REACT_APP_BACKEND_URL || "";
export const wsHost = (() => {
  try {
    return new URL(backend).host;
  } catch {
    return "";
  }
})();
const host = wsHost;

export const eaEndpoint = (slot) =>
  host ? `wss://${host}/api/ws/ea/${slot.slot_id}` : "";

export const eaSettings = (slot) => [
  ["SlotID", slot.slot_id, false],
  ["EASecret", slot.ea_secret, true],
  ["ServerURL", host ? `wss://${host}` : "", false],
  ["EAPath", "", false],
  ["SnapshotMs", "500", false],
  ["VerboseLog", "true", false],
];

const asText = (slot) =>
  eaSettings(slot)
    .map(([k, v]) => `${k} = ${v}`)
    .join("\n");

const Row = ({ name, value, secret, shown, onToggle, onCopy, copied, port }) => (
  <div className="flex items-center gap-2 py-1.5 border-b border-zinc-900 last:border-0">
    <span className="w-32 shrink-0 font-mono text-[11px] text-zinc-500">{name}</span>
    <code
      data-testid={`ea-value-${name}-${port}`}
      className="flex-1 min-w-0 truncate font-mono text-[11px] text-zinc-300 bg-black/50 border border-zinc-900 px-2 py-1 rounded-sm"
    >
      {value === "" ? <span className="text-zinc-600">(leave empty)</span> : secret && !shown ? "••••••••••••" : value}
    </code>
    {secret && (
      <button
        title={shown ? "Hide" : "Reveal"}
        data-testid={`ea-reveal-${port}`}
        onClick={onToggle}
        className="p-1 text-zinc-600 hover:text-zinc-200 transition-colors duration-200"
      >
        {shown ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    )}
    <button
      title={`Copy ${name}`}
      data-testid={`ea-copy-${name}-${port}`}
      disabled={value === ""}
      onClick={onCopy}
      className="p-1 text-zinc-600 hover:text-blue-400 disabled:opacity-30 transition-colors duration-200"
    >
      {copied ? <Check className="h-3.5 w-3.5 text-emerald-400" /> : <Copy className="h-3.5 w-3.5" />}
    </button>
  </div>
);

export const EaSetup = ({ slot }) => {
  const [shown, setShown] = useState(false);
  const [copied, setCopied] = useState(null);
  const port = slot.port;

  const copy = async (key, text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Clipboard blocked — reveal the value and copy it manually");
    }
  };

  return (
    <div
      className="border border-zinc-800 bg-black/40 rounded-sm p-3 space-y-2"
      data-testid={`ea-setup-${port}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 font-semibold">
          TradeDesk EA inputs
        </span>
        <span className="font-mono text-[10px] text-zinc-600">
          paste into the EA properties on this trader's MT5
        </span>
        <div className="flex-1" />
        <button
          data-testid={`ea-copy-all-${port}`}          onClick={() => {
            copy("all", asText(slot));
            toast.success(`EA settings for port ${port} copied`);
          }}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-zinc-800 hover:border-blue-600 text-[10px] uppercase tracking-[0.1em] font-semibold text-zinc-300 hover:text-blue-400 rounded-sm transition-colors duration-200"
        >
          {copied === "all" ? (
            <Check className="h-3 w-3 text-emerald-400" />
          ) : (
            <Copy className="h-3 w-3" />
          )}
          Copy all
        </button>
      </div>

      <div>
        {eaSettings(slot).map(([name, value, isSecret]) => (
          <Row
            key={name}
            name={name}
            value={value}
            secret={isSecret}
            shown={shown}
            port={port}
            copied={copied === name}
            onToggle={() => setShown((v) => !v)}
            onCopy={() => copy(name, value)}
          />
        ))}
      </div>

      <p className="text-[10px] text-zinc-600 font-mono leading-relaxed">
        In MT5: Tools &gt; Options &gt; Expert Advisors &gt; allow WebRequest, and add{" "}
        <span className="text-zinc-400">https://{host}</span>. SlotID is the slot id above.
        Attach the EA to any one chart with algo trading enabled.
      </p>
    </div>
  );
};
